// src/stores/auth.js
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { api, ApiError } from '@/api/client.js';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

export const useAuthStore = defineStore('auth', () => {
  const user    = ref(null);
  const checked = ref(false);

  async function checkAuth() {
    try      { user.value = await api.get('/auth/me'); }
    catch    { user.value = null; }
    finally  { checked.value = true; }
  }

  // ── Login ──────────────────────────────────────────────────────────────
  // Returns null on success, error string on failure.
  async function login(email) {
    let options;
    try {
      options = await api.post('/auth/webauthn/login/options', { email });
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Serverfejl — prøv igen';
    }
    let credential;
    try {
      credential = await startAuthentication({ optionsJSON: options });
    } catch (err) {
      if (err.name === 'NotAllowedError') return 'Nøgleafvisning — prøv igen';
      if (err.name === 'AbortError')      return null;
      return err.message || 'Sikkerhedsnøgle fejlede';
    }
    try {
      const result = await api.post('/auth/webauthn/login/verify', { email, credential });
      user.value = result.user;
      return null;
    } catch (err) {
      return err instanceof ApiError ? err.message : 'Verifikation fejlede';
    }
  }

  // ── Register ───────────────────────────────────────────────────────────
  // Returns { error } on failure, or { recoveryCodes, isAdmin } on success.
  async function register(name, email, deviceName) {
    let options;
    try {
      options = await api.post('/auth/webauthn/register/options', { name, email });
    } catch (err) {
      return { error: err instanceof ApiError ? err.message : 'Serverfejl — prøv igen' };
    }
    let credential;
    try {
      credential = await startRegistration({ optionsJSON: options });
    } catch (err) {
      if (err.name === 'NotAllowedError') return { error: 'Nøgleafvisning — prøv igen' };
      if (err.name === 'AbortError')      return { error: null };   // cancelled
      return { error: err.message || 'Sikkerhedsnøgle fejlede' };
    }
    try {
      const result = await api.post('/auth/webauthn/register/verify', {
        email, credential, deviceName: deviceName || 'YubiKey',
      });
      user.value = { email, name, isAdmin: result.isAdmin };
      return { recoveryCodes: result.recoveryCodes || [], isAdmin: result.isAdmin };
    } catch (err) {
      return { error: err instanceof ApiError ? err.message : 'Verifikation fejlede' };
    }
  }

  // ── Logout ──────────────────────────────────────────────────────────────
  async function logout() {
    try { await api.post('/auth/logout', {}); } catch { /* ignore */ }
    user.value = null;
  }

  return { user, checked, checkAuth, login, logout, register };
});
